import React, { useState, useRef, useEffect } from 'react';
import { Camera, Send, Loader2, ShoppingCart, CheckCircle, Sparkles } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useNutritionContext } from '../hooks/useNutritionContext';
import { useAuth } from '../context/AuthContext';
import { profileAPI } from '../services/apiService';
import { getCentralDate, getUserTimeZone } from '../utils/dateUtils';
import ReactMarkdown from 'react-markdown';
import { isIosApp } from '../utils/appPlatform';

const MAX_SOURCE_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_UPLOAD_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2048;

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('That photo could not be read. Please try another photo.'));
  img.src = url;
});

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error('That photo could not be prepared. Please try again.')),
    'image/jpeg',
    0.84
  );
});

const prepareNutritionImage = async (file) => {
  if (!file?.type?.startsWith('image/')) {
    throw new Error('Please choose a photo of food or a nutrition label.');
  }
  if (file.size > MAX_SOURCE_IMAGE_BYTES) {
    throw new Error('That photo is too large. Please use a photo smaller than 20 MB.');
  }

  const sourceUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(sourceUrl);
    const largestSide = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / largestSide);
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));
    const isAiReadyType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase());

    if (scale === 1 && isAiReadyType && file.size <= MAX_UPLOAD_IMAGE_BYTES) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('That photo could not be prepared. Please try again.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas);
    if (blob.size > MAX_UPLOAD_IMAGE_BYTES) {
      throw new Error('That photo is still too large. Please retake it a little closer.');
    }

    return new File([blob], `nutrition-photo-${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const NutritionCoach = () => {
  const { state, dispatch } = useNutritionContext();
  const { user } = useAuth();
  const iosApp = isIosApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi! I'm your AI Nutrition Coach powered by Kimi K2.6. I can help you with:

• **Should I eat this?** — Tell me what you're thinking of eating or upload a nutrition label photo
• **Check against your goals** — I'll search for accurate nutrition info and analyze if it fits your daily targets
• **Build your grocery list** — Get a personalized shopping list saved directly to your Grocery List tab
• **Review your grocery list** — I'll check if your planned groceries align with your goals

What would you like help with today?`
    }
  ]);

  // Load profile on mount so coach knows the user's name and can set up goals
  useEffect(() => {
    profileAPI.get().then(p => { if (p) setProfile(p); }).catch(() => {});
  }, []);

  // Auto-submit if navigated here with ?q= from the floating bubble
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSearchParams({}, { replace: true });
      // Small delay so the page finishes mounting
      setTimeout(() => handleSend(q), 300);
    }
  }, []);
  const [input, setInput] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [preparingImage, setPreparingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groceryStore, setGroceryStore] = useState(
    localStorage.getItem('preferredGroceryStore') || ''
  );
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => () => {
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError('');
    setPreparingImage(true);
    try {
      const preparedFile = await prepareNutritionImage(file);
      const previewUrl = URL.createObjectURL(preparedFile);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        previewUrlsRef.current.delete(imagePreview);
      }
      previewUrlsRef.current.add(previewUrl);
      setImage(preparedFile);
      setImagePreview(previewUrl);
    } catch (error) {
      setImage(null);
      setImagePreview(null);
      setImageError(error.message || 'That photo could not be prepared. Please try again.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setPreparingImage(false);
    }
  };

  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      previewUrlsRef.current.delete(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
    setImageError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle store name reply after coach asks for it
  const detectStoreReply = (text) => {
    const stores = ['walmart', 'kroger', 'publix', 'aldi', 'whole foods', 'costco', 'target', 'safeway', 'wegmans', 'heb', 'trader joe', 'meijer', 'food lion', 'giant', 'stop & shop'];
    const lower = text.toLowerCase();
    return stores.find(s => lower.includes(s)) || (lower.length < 30 && !lower.includes(' ') ? text.trim() : null);
  };

  // handleSend can be called directly with a string (from bubble ?q= param)
  const handleSend = async (overrideText) => {
    const text = typeof overrideText === 'string' ? overrideText : input.trim();
    if (!text && !image) return;
    setInput('');
    await handleSubmitCore(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() && !image) return;
    const text = input.trim();
    setInput('');
    await handleSubmitCore(text);
  };

  const handleSubmitCore = async (text) => {
    const userMessage = {
      role: 'user',
      content: text || 'Analyze and log this photo',
      image: imagePreview
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Check if user is answering the store question
    const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
    const isAnsweringStore = lastAssistantMsg?.content?.includes('grocery store do you shop at');
    let activeStore = groceryStore;

    if (isAnsweringStore) {
      const detectedStore = detectStoreReply(text);
      if (detectedStore) {
        activeStore = detectedStore;
        setGroceryStore(detectedStore);
        localStorage.setItem('preferredGroceryStore', detectedStore);
      }
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', text || 'Analyze this food or nutrition label and log the macros.');

      // Always calculate from today's user-local date, not the selected calendar date.
      const todayStr = getCentralDate();
      const todayEntries = (state.foodEntries || []).filter(e => e && e.date === todayStr);
      const todayTotals = todayEntries.reduce(
        (acc, e) => ({
          calories: acc.calories + (parseFloat(e.calories) || 0),
          protein:  acc.protein  + (parseFloat(e.protein)  || 0),
          carbs:    acc.carbs    + (parseFloat(e.carbs)    || 0),
          fat:      acc.fat      + (parseFloat(e.fat)      || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      const context = {
        currentDate: todayStr,
        timeZone: getUserTimeZone(),
        dailyGoals: state.dailyGoals,
        dailyTotals: todayTotals,
        remainingCalories: (state.dailyGoals?.calories || 0) - todayTotals.calories,
        remainingProtein:  (state.dailyGoals?.protein  || 0) - todayTotals.protein,
        remainingCarbs:    (state.dailyGoals?.carbs    || 0) - todayTotals.carbs,
        remainingFat:      (state.dailyGoals?.fat      || 0) - todayTotals.fat,
        groceryItems: state.groceryItems || [],
        groceryStore: activeStore,
        profile: profile || {}
      };
      formData.append('context', JSON.stringify(context));

      // Send last 10 messages for conversation context (skip the welcome message)
      const conversationHistory = updatedMessages.slice(1, -1).slice(-10).map(m => ({
        role: m.role,
        content: m.content || ''
      }));
      formData.append('conversation', JSON.stringify(conversationHistory));

      if (image) formData.append('image', image, image.name || 'nutrition-photo.jpg');

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ai/coach`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 403 && data.error === 'pro_required') {
        setMessages(prev => [...prev, { role: 'assistant', content: '__upgrade__' }]);
        setLoading(false);
        removeImage();
        return;
      }

      if (!response.ok && !data.response) {
        throw new Error(data.error || 'Failed to get coach response');
      }

      // Handle grocery list saved action
      if (data.action === 'grocery_list_saved') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          grocerySaved: true,
          itemsSaved: data.itemsSaved
        }]);
        // Refresh grocery list in context
        dispatch({ type: 'LOAD_USER_DATA' });
      } else if (data.action === 'meal_logged') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response
        }]);
        // Refresh food entries so dashboard updates immediately
        dispatch({ type: 'LOAD_USER_DATA' });
      } else if (data.action === 'goals_set') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response
        }]);
        // Update goals in context so dashboard shows them immediately
        if (data.goals) {
          dispatch({ type: 'UPDATE_DAILY_GOALS', payload: data.goals });
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response
        }]);
      }

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I ran into an issue: ${error.message}. Please try again.`
      }]);
    } finally {
      setLoading(false);
      setImage(null);
      setImagePreview(null);
      setImageError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">AI Nutrition Coach</h1>
              <p className="text-blue-100 text-xs mt-0.5">Powered by Kimi K2.6 · Web Search Enabled</p>
            </div>
            {groceryStore && (
              <div className="text-right">
                <p className="text-xs text-blue-200">Shopping at</p>
                <p className="text-sm font-semibold capitalize">{groceryStore}</p>
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              }`}>
                {message.image && (
                  <img src={message.image} alt="Uploaded" className="mb-2 rounded max-w-full h-auto" />
                )}
                {message.content === '__upgrade__' && iosApp ? (
                  <div className="text-sm">
                    <p className="font-semibold mb-2">Unlock the AI Nutrition Coach with NutriTrack Pro.</p>
                    <p className="text-gray-600 dark:text-gray-400 mb-3 text-xs">Subscribe securely with your Apple Account and start using photo analysis, label scanning, and unlimited AI coaching.</p>
                    <Link to="/upgrade" className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors">
                      <Sparkles className="w-3.5 h-3.5" /> View Apple Plans
                    </Link>
                  </div>
                ) : message.content === '__upgrade__' && (user?.subscriptionStatus !== 'pro' && user?.subscriptionStatus !== 'premium') ? (
                  <div className="text-sm">
                    <p className="font-semibold mb-2">The AI Nutrition Coach requires a Pro subscription.</p>
                    <p className="text-gray-600 dark:text-gray-400 mb-3 text-xs">Get unlimited AI coaching, label scanning, grocery list building and more for just $4.99/month.</p>
                    <Link to="/upgrade" className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors">
                      <Sparkles className="w-3.5 h-3.5" /> Upgrade to Pro
                    </Link>
                  </div>
                ) : message.content === '__upgrade__' ? (
                  <div className="text-sm">
                    <p className="font-semibold mb-2">Accessing Pro features...</p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">If you see this message, please try refreshing the page or checking your connection.</p>
                  </div>
                ) : null}
                <div className={`text-sm prose prose-sm max-w-none dark:prose-invert ${message.content === '__upgrade__' ? 'hidden' : ''}`}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                      li: ({ children }) => <li>{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      h1: ({ children }) => <h1 className="text-base font-bold mb-1">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-sm font-bold mb-1">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
                {message.grocerySaved && (
                  <div className="mt-2 flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                    <CheckCircle className="w-4 h-4" />
                    {message.itemsSaved} items saved to your Grocery List tab
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300 italic">
                  Hang tight — checking multiple sources for accuracy...
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img src={imagePreview} alt="Preview" className="h-20 rounded border border-gray-300" />
              <button
                onClick={removeImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
              >
                ×
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex gap-2">
            <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || preparingImage}
              className="p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Upload nutrition label photo"
            >
              {preparingImage
                ? <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                : <Camera className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </button>

            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about nutrition..."
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />

            <button
              type="submit"
              disabled={loading || preparingImage || (!input.trim() && !image)}
              className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>

          {imageError && (
            <p className="mt-2 text-xs font-medium text-red-600" role="alert">{imageError}</p>
          )}

          <div className="mt-2 flex gap-2 flex-wrap">
            <button
              onClick={() => setInput('Should I eat this? ')}
              className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
            >
              Should I eat this?
            </button>
            <button
              onClick={() => setInput('I just ate ')}
              className="px-3 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-full transition-colors"
            >
              Log a meal
            </button>
            <button
              onClick={() => setInput('Help me build a grocery list for the week')}
              className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors flex items-center gap-1"
            >
              <ShoppingCart className="w-3 h-3" /> Build grocery list
            </button>
            <button
              onClick={() => setInput('Review my current grocery list')}
              className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
            >
              Review grocery list
            </button>
            <button
              onClick={() => setInput('Find me a recipe for ')}
              className="px-3 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-full transition-colors"
            >
              Find a recipe
            </button>
            {groceryStore && (
              <button
                onClick={() => {
                  setGroceryStore('');
                  localStorage.removeItem('preferredGroceryStore');
                }}
                className="px-3 py-1 text-xs bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 rounded-full transition-colors"
              >
                Change store
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionCoach;
